import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { formatDate } from "../../lib/date";
import { toast } from "sonner";
import {
  Radar, Plus, Trash2, RefreshCw, Loader2, Globe, Rss, ExternalLink,
  Sparkles, Mail, AlertTriangle, Building2, Newspaper, Eye, Power,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import AIStatusBadge from "./AIStatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_TONE = {
  Product: "#8A6D00", Pricing: "#C23A00", Campaign: "#1D4ED8", Hiring: "#0E6B3F",
  Partnership: "#3B82F6", PR: "#6B5600", Other: "#8E8E93",
};
const threatTone = (t) => (t >= 70 ? "#C23A00" : t >= 40 ? "#6B5600" : "#8E8E93");

function Stat({ icon: Icon, label, value, tone = "#8A6D00", testid }) {
  return (
    <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${tone}1A` }}>
          <Icon className="w-5 h-5" style={{ color: tone }} />
        </div>
        <div>
          <div className="text-xl font-semibold text-[#1A1A1A]">{value}</div>
          <div className="text-xs text-[#8A857A]">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

const emptySource = { name: "", url: "", kind: "competitor", competitor_name: "" };

export default function CompetitorSpy() {
  const [overview, setOverview] = useState(null);
  const [sources, setSources] = useState([]);
  const [signals, setSignals] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [digesting, setDigesting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptySource);
  const [saving, setSaving] = useState(false);
  const [filterKind, setFilterKind] = useState("all");

  const fetchAll = useCallback(async () => {
    try {
      const [o, s, sig, d] = await Promise.all([
        axios.get(`${API}/competitor-spy/overview`),
        axios.get(`${API}/competitor-spy/sources`),
        axios.get(`${API}/competitor-spy/signals?limit=100`),
        axios.get(`${API}/competitor-spy/digests/latest`),
      ]);
      setOverview(o.data);
      setSources(s.data.sources || []);
      setSignals(sig.data.signals || []);
      setDigest(d.data.digest || null);
    } catch (e) {
      toast.error("Failed to load Competitor Spy");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addSource = async () => {
    if (!form.url.trim()) { toast.error("A URL is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/competitor-spy/sources`, {
        name: form.name.trim(), url: form.url.trim(), kind: form.kind,
        competitor_name: form.kind === "competitor" ? form.competitor_name.trim() : "",
      });
      toast.success("Source added");
      setAddOpen(false); setForm(emptySource);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add source");
    } finally { setSaving(false); }
  };

  const toggleSource = async (s) => {
    try { await axios.put(`${API}/competitor-spy/sources/${s.id}`, { active: !s.active }); await fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update source"); }
  };

  const removeSource = async (id) => {
    if (!window.confirm("Remove this source and its captured signals?")) return;
    try { await axios.delete(`${API}/competitor-spy/sources/${id}`); toast.success("Source removed"); await fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove source"); }
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const r = await axios.post(`${API}/competitor-spy/check`);
      toast.success(`Scanned ${r.data.sources} source(s) — ${r.data.new_signals} new signal(s)`);
      await fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  const runDigest = async () => {
    setDigesting(true);
    try {
      const r = await axios.post(`${API}/competitor-spy/run-digest`, {}, { timeout: 120000 });
      toast.success(r.data.emailed ? `Digest compiled & emailed to ${r.data.emailed} leader(s)` : "Digest compiled");
      await fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Digest failed"); }
    finally { setDigesting(false); }
  };

  const markSeen = async (id) => {
    try { await axios.post(`${API}/competitor-spy/signals/${id}/seen`); setSignals((xs) => xs.map((x) => x.id === id ? { ...x, seen: true } : x)); }
    catch (e) { /* ignore */ }
  };

  const shownSignals = signals.filter((s) => filterKind === "all" || s.kind === filterKind);

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-[#8A857A]" data-testid="spy-loading"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading Competitor Spy…</div>;
  }

  return (
    <div className="space-y-5" data-testid="competitor-spy">
      {/* Header + actions */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FFE9E0] flex items-center justify-center">
            <Radar className="w-5 h-5 text-[#C23A00]" />
          </div>
          <div>
            <h3 className="heading text-lg text-[#1A1A1A] tracking-tight">Competitor Spy</h3>
            <p className="text-sm text-[#59554D]">Monitors competitor &amp; industry websites, then AI briefs you daily on what changed</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AIStatusBadge />
          <Button variant="outline" onClick={scanNow} disabled={scanning} className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="spy-scan-btn">
            {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Scan now
          </Button>
          <Button onClick={runDigest} disabled={digesting} className="bg-[#C23A00] hover:bg-[#FFE9E0] text-[#1A1A1A] font-medium" data-testid="spy-digest-btn">
            {digesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Run AI digest
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Globe} label="Active sources" value={overview?.active_sources ?? 0} tone="#8A6D00" testid="spy-stat-sources" />
        <Stat icon={Newspaper} label="Signals (7 days)" value={overview?.last_7_days ?? 0} tone="#1D4ED8" testid="spy-stat-week" />
        <Stat icon={Eye} label="Unread" value={overview?.unseen ?? 0} tone="#6B5600" testid="spy-stat-unseen" />
        <Stat icon={Radar} label="Total signals" value={overview?.signals ?? 0} tone="#0E6B3F" testid="spy-stat-total" />
      </div>

      {(!overview?.llm_ready || !overview?.email_ready) && (
        <div className="rounded-lg border border-[#FFF7D1] bg-[#FFF7D1] p-3 text-xs text-[#6B5600] flex items-center gap-2" data-testid="spy-config-warn">
          <AlertTriangle className="w-4 h-4" />
          {!overview?.llm_ready && "AI digest disabled (LLM key missing). "}
          {!overview?.email_ready && "Email alerts disabled (Resend not configured)."}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: signals feed */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Signal feed</div>
            <Select value={filterKind} onValueChange={setFilterKind}>
              <SelectTrigger className="w-[160px] h-8 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-xs" data-testid="spy-filter-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="competitor">Competitors only</SelectItem>
                <SelectItem value="industry">Industry only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {shownSignals.length === 0 ? (
            <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="spy-empty">
              <CardContent className="p-10 text-center">
                <Radar className="w-8 h-8 text-[#C9C3B6] mx-auto mb-3" />
                <p className="text-sm text-[#59554D]">No signals yet.</p>
                <p className="text-xs text-[#8A857A] mt-1">Add a competitor or industry source, then hit “Scan now”.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {shownSignals.map((s) => (
                <div key={s.id} className={`rounded-lg border p-3 ${s.seen ? "border-[#F4F2ED] bg-[#FFFFFF]" : "border-[#DAD5CA] bg-[#FFFFFF]"}`} data-testid={`spy-signal-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {s.threat_score != null && (
                          <span className="text-[10px] font-bold text-[#1A1A1A] rounded px-1.5 py-0.5" style={{ background: threatTone(s.threat_score) }} data-testid={`spy-threat-${s.id}`}>{s.threat_score}</span>
                        )}
                        {s.category && (
                          <Badge className="text-[10px] border-0" style={{ background: `${CAT_TONE[s.category] || "#8E8E93"}22`, color: CAT_TONE[s.category] || "#8E8E93" }}>{s.category}</Badge>
                        )}
                        <span className="text-[11px] text-[#8A857A] inline-flex items-center gap-1">
                          {s.kind === "competitor" ? <Building2 className="w-3 h-3" /> : <Newspaper className="w-3 h-3" />}
                          {s.competitor_name || (s.kind === "industry" ? "Industry" : "Competitor")} · {s.source_name}
                        </span>
                      </div>
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1A1A1A] hover:text-[#8A6D00] inline-flex items-start gap-1" data-testid={`spy-signal-link-${s.id}`}>
                        {s.title} <ExternalLink className="w-3 h-3 mt-0.5 shrink-0 opacity-60" />
                      </a>
                      {s.ai_note && <p className="text-xs text-[#59554D] mt-1">{s.ai_note}</p>}
                    </div>
                    {!s.seen && (
                      <Button size="sm" variant="ghost" className="h-7 text-[#8A857A] hover:text-[#1A1A1A] shrink-0" onClick={() => markSeen(s.id)} data-testid={`spy-seen-${s.id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: latest digest + sources */}
        <div className="space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#C23A00]" /> Latest AI briefing</div>
            <Card className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid="spy-digest-card">
              <CardContent className="p-4">
                {digest ? (
                  <>
                    <div className="text-[11px] text-[#8A857A] mb-2">{formatDate(digest.date)} · {digest.signal_count} item(s)</div>
                    <p className="text-sm text-[#59554D] whitespace-pre-line leading-relaxed">{digest.digest_text}</p>
                  </>
                ) : (
                  <p className="text-xs text-[#8A857A]">No briefing yet — run the AI digest once you have signals.</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-[#8A857A] font-semibold">Sources ({sources.length})</div>
              <Button size="sm" variant="ghost" className="h-7 text-[#8A6D00] hover:bg-[#FFF8D6]" onClick={() => { setForm(emptySource); setAddOpen(true); }} data-testid="spy-add-source">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {sources.length === 0 ? (
              <p className="text-xs text-[#8A857A]">No sources yet. Add a competitor website, blog, changelog, or an industry news page (RSS auto-detected).</p>
            ) : (
              <div className="space-y-2">
                {sources.map((s) => (
                  <div key={s.id} className="rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-3" data-testid={`spy-source-${s.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm text-[#1A1A1A] truncate flex items-center gap-1.5">
                          {s.source_type === "rss" ? <Rss className="w-3.5 h-3.5 text-[#6B5600]" /> : <Globe className="w-3.5 h-3.5 text-[#8A6D00]" />}
                          {s.name}
                        </div>
                        <div className="text-[11px] text-[#8A857A] truncate">{s.url}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="text-[10px] border-0 bg-[#DAD5CA] text-[#59554D]">{s.kind === "competitor" ? (s.competitor_name || "Competitor") : "Industry"}</Badge>
                          {s.last_status === "error"
                            ? <span className="text-[10px] text-[#C23A00]">error</span>
                            : <span className="text-[10px] text-[#8A857A]">{s.signal_count || 0} signals</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="icon" variant="ghost" className={`h-7 w-7 ${s.active ? "text-[#0E6B3F]" : "text-[#8A857A]"} hover:bg-[#F4F2ED]`} onClick={() => toggleSource(s)} title={s.active ? "Active — click to pause" : "Paused — click to enable"} data-testid={`spy-source-toggle-${s.id}`}>
                          <Power className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#8A857A] hover:text-[#C23A00]" onClick={() => removeSource(s.id)} data-testid={`spy-source-del-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add source dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg" data-testid="spy-add-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Radar className="w-4 h-4 text-[#C23A00]" /> Add monitored source</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#59554D]">Type</label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="spy-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                  <SelectItem value="competitor">Competitor</SelectItem>
                  <SelectItem value="industry">Industry news</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.kind === "competitor" && (
              <div>
                <label className="text-xs text-[#59554D]">Competitor name</label>
                <Input value={form.competitor_name} onChange={(e) => setForm({ ...form, competitor_name: e.target.value })}
                  placeholder="e.g. Assignar" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="spy-form-competitor" />
              </div>
            )}
            <div>
              <label className="text-xs text-[#59554D]">Label</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Assignar blog" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="spy-form-name" />
            </div>
            <div>
              <label className="text-xs text-[#59554D]">URL (website, blog, changelog, or RSS feed)</label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://competitor.com/blog" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="spy-form-url" />
              <p className="text-[11px] text-[#8A857A] mt-1">Tip: RSS/blog feeds give the cleanest signals. We auto-detect feeds; other pages are watched for changes.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={addSource} disabled={saving} className="bg-[#C23A00] hover:bg-[#FFE9E0] text-[#1A1A1A] font-medium" data-testid="spy-form-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Add source
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
